<template>
  <div :class="[embeddedMode ? '' : 'mx-6 my-4']">
    <div class="md:flex md:items-center md:justify-between md:space-x-5">
      <div v-if="!embeddedMode" class="flex items-center space-x-5">
        <div class="flex-shrink-0">
          <div class="relative bg-emerald-600/70 rounded-xl p-1">
            <mdicon name="chart-box-outline" size="62" class="text-emerald-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="">
          <h1 class="mb-1 text-2xl font-bold text-gray-900">Volume</h1>
          <p class="text-sm font-medium text-gray-500">Bot Conversations Volume</p>
        </div>
      </div>
      <div v-else class="flex items-center space-x-3 mb-1">
        <div class="flex-shrink-0">
          <div class="relative bg-violet-600/70 rounded-md p-1">
            <mdicon name="chart-box-outline" size="30" class="text-violet-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="">
          <h1 class="mb-1 text-xl font-semibold text-gray-800">Bot Conversations Volume</h1>
        </div>
      </div>
      <div class="flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
        <ProgressButton ref="analyzeProgressButton" title="Refresh" icon="refresh" @click="loadData"></ProgressButton>
        <button
          type="button"
          class="flex items-center gap-x-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          @click="onExport"
        >
          <mdicon name="download-outline" class="h-5 w-5 mr-1" aria-hidden="true"></mdicon>
          Export
        </button>
        <!--
        <button
          v-if="rbac.canUseDataAssistant"
          v-tooltip.bottom="'Data Assistant'"
          @click="onToggleDA"
          :class="[
            dataAssistantStore.visible ? 'text-slate-100 bg-emerald-600/70 hover:bg-emerald-500/70 border-transparent shadow-gradient-glow-green' : 'text-slate-600 hover:bg-slate-100/70 hover:shadow-gradient-glow-green',
            'px-2 mr-2 rounded border',
          ]"
        >
          <mdicon name="creation" size="22" aria-hidden="true"></mdicon>
        </button>
        -->
      </div>
    </div>

    <FiltersList class="mt-2" @select-filter="onSelectFilter" @show-filters="showFilters"></FiltersList>

    <div class="overflow-hidden mt-2 mb-12">
      <div class="flex items-center justify-between border-b border-gray-300 pb-1">
        <div class="flex items-center">
          <mdicon name="text" size="18" class="rounded-md bg-emerald-600/70 p-1 mr-2 text-white" aria-hidden="true"></mdicon>
          <h2 class="text-base font-semibold leading-7 text-gray-900">
            <span class="text-lg text-slate-800 font-semibold">{{ totalConversations }}</span> conversations over
            <span class="text-md text-slate-700">{{ analyzedDateRange }} (UTC)</span>
          </h2>
        </div>
        <div class="flex items-center">
          <SelectButton
            v-tooltip.top="'Group time by ...'"
            v-model="selectedGroupingInterval"
            :options="groupingIntervalOptions"
            optionLabel="title"
            optionValue="value"
            :allowEmpty="false"
            aria-labelledby="basic"
            @change="onChangeGroupingInterval"
          />
        </div>
      </div>
      <DbUplot
        v-if="totalConversations > 0"
        class="w-full h-[160px] mt-1 bg-gray-100/50 rounded-xl"
        :data="uPlotConversationsData"
        :options="uPlotOptionsConvs"
        @db-event="handleDbEvent"
        :tooltip="true"
        :title="`${totalConversations} Conversations`"
        tz="Etc/UTC"
      ></DbUplot>
    </div>

    <!-- TODO take sidebar width(left) and data analyzer width(right) dynamically in calc -->
    <div class="overflow-auto">
      <DataTable :value="tableData" sortField="ts" :sortOrder="-1" removableSort size="small" class="font-source" rowHover>
        <Column field="ts" header="Date" sortable>
          <template #body="slotProps">
            <router-link :to="{ path: '/bots/conversations', query: { filter: getFilterDates(slotProps?.data) } }" class="px-2 py-1 flex items-center hover:underline">
              <div class="cursor-pointer font-medium">{{ slotProps?.data?.date }}</div>
              <mdicon name="open-in-new" size="20" class="ml-2 text-gray-300"></mdicon>
            </router-link>
          </template>
        </Column>

        <Column field="count" sortable header="Conversations">
          <template #body="slotProps">
            <router-link :to="{ path: '/bots/conversations', query: { filter: getFilterDates(slotProps?.data) } }" class="flex items-center hover:underline w-full text-right">
              <div :class="['w-full cursor-pointer', getValueClass(slotProps?.data?.count)]">{{ slotProps?.data?.count }}</div>
            </router-link>
          </template>
        </Column>
        <Column field="avgCSat" sortable header="Avg CSat">
          <template #body="slotProps">
            <div :class="['w-full text-right', getValueClass(slotProps?.data?.avgCSat)]">
              {{ slotProps?.data?.avgCSat ? slotProps.data.avgCSat.toFixed(2) : 0 }}
            </div>
          </template>
        </Column>
        <Column field="escalated" sortable header="Escalated">
          <template #body="slotProps">
            <div :class="['w-full text-right', getValueClass(slotProps?.data?.escalated)]">
              {{ slotProps?.data?.escalated || 0 }}
            </div>
          </template>
        </Column>
        <Column field="escalatedPct" sortable header="Escalated %%">
          <template #body="slotProps">
            <div :class="['w-full text-right', getValueClass(slotProps?.data?.escalatedPct)]">{{ (slotProps?.data?.escalatedPct || 0).toFixed(0) }}%</div>
          </template>
        </Column>
        <Column field="avgTTA" sortable header="Avg TTA">
          <template #body="slotProps">
            <div :class="['w-full text-right', getValueClass(slotProps?.data?.avgTTA)]">
              {{ slotProps?.data?.avgTTA ? slotProps.data.avgTTA.toFixed(2) : 0 }}
            </div>
          </template>
        </Column>
        <!--
        <template #footer>
          <div class="flex justify-content-start">
            <Button icon="pi pi-refresh" label="Reload" severity="warning" size="small" />
          </div>
        </template>
        -->
      </DataTable>
    </div>

    <EditFiltersSidebar v-model:visible="filtersOpen"></EditFiltersSidebar>
  </div>
</template>

<script setup>
  import { onMounted, onBeforeUnmount, ref, reactive, computed, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useLayoutStore } from '@/store/LayoutStore';
  import { useBotConversationSearchStore } from '@/store/BotConversationSearchStore';
  import { useFiltersStore } from '@/store/FiltersStore';
  import { useDataAssistantStore } from '@/store/DataAssistantStore';
  import { useRbacStore } from '@/store/RBACStore';
  import { useSettingsStore } from '@/store/SettingsStore';
  import { stdBotConversationFilters } from '@/analytics/filtersDefs';
  import { ArrowDownCircleIcon, HashtagIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/20/solid';
  import ProgressButton from '@/components/Buttons/ProgressButton.vue';
  import { useDateRangeStore } from '@/store/DaterangeStore';
  import DbUplot from '@/components/Charts/DbUplot.vue';
  import uPlot from 'uplot';
  import EditFiltersSidebar from '@/components/Filters/EditFiltersSidebar.vue';
  import FiltersList from '@/components/Filters/FiltersList.vue';
  import dbColors from '@/components/Charts/dbcolors';
  import { observable10Colors, tableauColors } from '@/components/Charts/dbcolors';
  import { formatSeconds } from '@/utils/formatSeconds';
  import TreeTable from 'primevue/treetable';
  import DataTable from 'primevue/datatable';
  import Column from 'primevue/column';
  import Button from 'primevue/button';
  import SelectButton from 'primevue/selectbutton';
  import DAConversation from '@/components/DataAssistant/DAConversation.vue';
  import { utils, writeFileXLSX } from 'xlsx';
  import DbSparkLine from '@/components/Charts/DbSparkLine.vue';
  import DbHorizon from '@/components/Charts/DbHorizon.vue';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import isoWeek from 'dayjs/plugin/isoWeek';

  import * as d3 from 'd3';
  import * as d3Color from 'd3-color';

  dayjs.extend(utc);
  dayjs.extend(isoWeek);

  const route = useRoute();
  const router = useRouter();
  const settingsStore = useSettingsStore();
  const layout = useLayoutStore();
  const conversationSearchStore = useBotConversationSearchStore();
  const dateRangeStore = useDateRangeStore();
  const filtersStore = useFiltersStore();
  const rbac = useRbacStore();
  const dataAssistantStore = useDataAssistantStore();

  const loading = ref(false);

  // Report Table data
  const tableData = ref([]);

  const totalConversations = ref(0);
  const analyzedDateRange = ref('');
  const analyzedDateRangeFn = ref('');

  let timelineMax = 0;
  let timelineColorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, 10]);

  // Active date range
  const rangeFrom = ref(0);
  const rangeTo = ref(0);

  // Grouping intervals
  // todo consider moving to dateRangeStore
  const groupingIntervalOptions = computed(() => {
    const opts = [
      { title: 'Day', value: '1d', endOf: 'day', enabled: true },
      { title: 'Week', value: '1w', endOf: 'isoWeek', enabled: dateRangeStore.durationDays >= 7 },
      { title: 'Month', value: '1M', endOf: 'month', enabled: dateRangeStore.durationDays >= 28 },
    ];
    return opts.filter((x) => x.enabled);
  });
  // todo take from store ?
  const selectedGroupingInterval = ref('1d');
  const selectedGroupingIntervalData = computed(() => {
    return groupingIntervalOptions.value.find((x) => x.value === selectedGroupingInterval.value);
  });

  // Data assistant
  const daMessages = ref([]);

  const embeddedMode = ref(route.meta?.embeddedMode === 'true');
  const analyzeProgressButton = ref(null);
  const filtersOpen = ref(false);

  const dataTitle = 'Handoff reasons';

  const dataDescription =
    'Data contains customer service chatbot handoff reasons.\n' +
    'Handoff happens when chatbot escalates conversation to a human, and reason describes why.\n' +
    'Each data record contains a reason (field "reason"), and how many times handoff with this reason occurred (field "count").\n' +
    'Some reasons may be worded differently but mean the same thing.\n' +
    'In your analysis always account for that, and generalize your insights accounting for that.\n';

  const suggestedQuestions = [
    'What insights can you derive from this data?',
    'What are the most frequent handoff reasons?',
    'What information is missing the most so Bot had to handoff?',
    'What can be done to reduce handoffs ?',
  ];

  watch(filtersStore.allFilters, (newValue) => {
    if (loading.value) {
      return;
    }
    console.log(`Filters changed: ${JSON.stringify(newValue)}`);
    loadData();
  });

  watch(
    () => dateRangeStore.timeIntervalChanged,
    (newValue) => {
      console.log(`Date range changed: ${JSON.stringify(newValue)}`);
      selectedGroupingInterval.value = dateRangeStore.groupingCalendarInterval;
      loadData();
    },
  );

  function onChangeGroupingInterval() {
    console.log(`Grouping interval changed: ${selectedGroupingInterval.value}`);
    loadData();
  }

  const uPlotConversationsData = ref([]);
  const uPlotOptionsConvs = computed(() => {
    return {
      //height: 200,
      legend: {
        show: true,
      },
      tzDate: (ts) => uPlot.tzDate(new Date(ts * 1e3), 'Etc/UTC'),
      scales: {
        x: { time: true },
      },
      series: [
        {},
        {
          label: 'Total',
          paths: uPlot.paths.bars({ align: 0 }),
          stroke: dbColors.hex2RGBA(observable10Colors[0], 0.7), //'rgba(78, 121, 167, 0.1 )',
          fill: dbColors.hex2RGBA(observable10Colors[0], 0.7), //'rgba(78, 121, 167, 0.4)',
        },
        {
          label: 'Escalated',
          paths: uPlot.paths.bars({ align: 0 }),
          stroke: dbColors.hex2RGBA(observable10Colors[1], 1), //'rgba(78, 121, 167, 0.1 )',
          fill: dbColors.hex2RGBA(observable10Colors[1], 1), //'rgba(78, 121, 167, 0.4)',
        },
      ],
    };
  });

  async function loadData() {
    analyzeProgressButton.value.startProgress();
    loading.value = true;

    // Align time frame on beginning/end of day / week / month
    const originalFrom = dateRangeStore.fromTs;
    const originalTo = dateRangeStore.toTs;
    rangeFrom.value = originalFrom; // dayjs(originalFrom).utc().startOf(selectedGroupingIntervalData.value.endOf).valueOf();
    rangeTo.value = originalTo; // dayjs(originalTo).utc().endOf(selectedGroupingIntervalData.value.endOf).valueOf();

    const calendarInterval = selectedGroupingInterval.value;

    const groupings = [
      {
        name: 'timeline',
        title: 'Date',
        type: 'date_histogram',
        calendar_interval: calendarInterval,
        //extended_bounds: { min: rangeFrom.value, max: rangeTo.value },
        time_zone: 'Etc/UTC',
        groupings: [
          {
            name: 'csat',
            title: 'CSat',
            type: 'stats',
            field: 'CSat',
          },
          {
            name: 'tta',
            title: 'TTA',
            type: 'stats',
            field: 'state.timeToAgent',
          },
          { name: 'escalated', type: 'filter', query: { term: { 'state.escalated': true } } },
        ],
      },
      {
        name: 'timehistogram',
        title: 'Date',
        type: 'date_histogram',
        calendar_interval: '1d', // todo calculate that - if less then 14 days, switch to hours ?
        time_zone: 'Etc/UTC',
        groupings: [{ name: 'escalated', type: 'filter', query: { term: { 'state.escalated': true } } }],
      },
    ];

    // ???
    if (selectedGroupingIntervalData.value?.offset) {
      groupings[0].offset = selectedGroupingIntervalData.value.offset;
    }

    const aggregateResult = await conversationSearchStore.aggregate(groupings);

    // todo if aggregating by calendar_interval with offset, may need to strip unwanted time bucket at the beginning
    //  like by week, with -1d offset (starting sunday) will produce one extra week bucket at the beginning

    /*
    const aggregateResult = await conversationSearchStore.aggregate(groupings, {
      from: rangeFrom.value,
      to: rangeTo.value,
      interval: dateRangeStore.timeInterval.interval, // Not applicable - calendar interval will be used in groupings
    });*/

    processAggregateResult(aggregateResult);
    loading.value = false;
    analyzeProgressButton.value.stopProgress();
  }

  function timelineKeyToIntervalName(key) {
    let name = dayjs(key).utc().format('DD/MMM/YYYY'); // default
    switch (selectedGroupingInterval.value) {
      case '1d': {
        name = dayjs(key).utc().format('DD/MMM/YYYY');
        break;
      }
      case '1w': {
        // todo partial
        name = 'Week ' + dayjs(key).utc().format('DD/MMM');
        break;
      }
      case '1M': {
        // todo partial
        name = dayjs(key).utc().format('MMM YYYY');
        break;
      }
    }
    if (key < rangeFrom.value) {
      name = name + ' (partial)';
    }
    return name;
  }

  function convertAggregationsToReportData(aggregations) {
    const tData = [];
    const uPlotConvData = [[], [], []];
    totalConversations.value = 0;
    analyzedDateRange.value = '';
    analyzedDateRangeFn.value = '';

    if (!Array.isArray(aggregations?.timeline?.buckets) || aggregations.timeline.buckets.length <= 0) {
      tableData.value = tData;
      uPlotConversationsData.value = uPlotConvData;
      return;
    }
    const timelineBuckets = aggregations.timeline.buckets;
    const timeHistogramBuckets = Array.isArray(aggregations?.timehistogram?.buckets) ? aggregations.timehistogram.buckets : [];

    const startDate = rangeFrom.value;
    const endDate = rangeTo.value;

    analyzedDateRange.value = `${dayjs(startDate).utc().format('YYYY-MM-DD')} - ${dayjs(endDate).utc().format('YYYY-MM-DD')}`;
    analyzedDateRangeFn.value = `${dayjs(startDate).utc().format('YYYY-MM-DD')}_${dayjs(endDate).utc().format('YYYY-MM-DD')}`;

    let total = 0;
    timelineBuckets.forEach((bucket) => {
      const count = bucket?.doc_count || 0;
      total += count;
      const esc = bucket.escalated?.doc_count || 0;
      const escPct = count > 0 && esc > 0 ? Math.floor((esc / count) * 100) : 0;
      const row = {
        // todo if key is less then rangeFrom, then set to rangeFrom
        ts: bucket.key,
        date: timelineKeyToIntervalName(bucket.key),
        count: count,
        avgCSat: bucket.csat?.avg || 0,
        escalated: esc,
        escalatedPct: escPct,
        avgTTA: bucket.tta?.avg || 0,
      };
      tData.push(row);
    });

    timeHistogramBuckets.forEach((bucket) => {
      const count = bucket?.doc_count || 0;
      const esc = bucket.escalated?.doc_count || 0;
      uPlotConvData[0].push(Math.floor(bucket.key / 1000));
      uPlotConvData[1].push(count === 0 ? null : count);
      uPlotConvData[2].push(esc === 0 ? null : esc);
    });

    totalConversations.value = total;
    tableData.value = tData;
    uPlotConversationsData.value = uPlotConvData;
  }

  function processAggregateResult(aggregateResult) {
    timelineMax = 0;
    timelineColorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, 10]);

    if (!aggregateResult.success) {
      console.log(`Error aggregating data: ${aggregateResult.error}`);
      return;
    }

    let timelineData = aggregateResult?.result?.aggregations || null;

    convertAggregationsToReportData(timelineData);
  }

  function handleDbEvent(event) {
    switch (event.type) {
      case 'zoom': {
        console.log(`Handle Zoom`, event);
        let from = event?.minDate || null;
        let to = event?.maxDate || null;
        if (from && to) {
          dateRangeStore.setDateRange(from, to);
          loadData();
        }
        break;
      }
    }
  }

  function onSelectFilter(filter) {
    // TODO pass which filter to make active
    showFilters();
  }

  function showFilters() {
    filtersOpen.value = true;
  }

  function getFilterQueryConversations(data) {
    return [encodeURIComponent(`state.handoffReason:${data.reason}`)];
  }

  function getFilterCategories(node) {
    const categoriesFilters = (node.path || [])
      .map((p, idx) => {
        return idx < 3 ? encodeURIComponent(`${categoriesFiltersNames[idx]}:${p !== 'N/A' ? p : null}`) : null;
      })
      .filter((x) => x);
    return categoriesFilters;
  }

  function getFilterDates(item) {
    const startDate = item.ts;
    const endDate = dayjs(startDate).utc().endOf(selectedGroupingIntervalData.value.endOf).valueOf();
    return [encodeURIComponent(`from:${startDate}`), encodeURIComponent(`to:${endDate}`)];
  }

  function getValueClass(val) {
    if (!val || val === 0) {
      return 'text-slate-400';
    }
    return 'text-slate-700 font-bold';
  }

  function onExport() {
    const exportData = tableData.value.map((x) => {
      return {
        Date: x.date,
        Conversations: x.count,
        'Avg CSat': (x?.avgCSat || 0).toFixed(2),
        Escalated: x.escalated,
        'Escalated %%': x.escalatedPct,
        'Avg TTA (seconds)': (x?.avgTTA || 0).toFixed(2),
      };
    });
    const wsCategories = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, wsCategories, 'Volume');
    writeFileXLSX(wb, `VolumeReport_${settingsStore.tenantName}_${analyzedDateRangeFn.value}.xlsx`);
  }

  function onToggleDA() {
    if (dataAssistantStore.visible) {
      dataAssistantStore.hide();
    } else {
      dataAssistantStore.show();
    }
  }

  watch(
    () => rbac.canUseDataAssistant,
    (newValue) => {
      if (newValue) {
        dataAssistantStore.hide();
        //dataAssistantStore.show();
      } else {
        dataAssistantStore.hide();
      }
    },
  );

  onMounted(() => {
    filtersStore.initFilters('botconv', stdBotConversationFilters);
    dataAssistantStore.init({
      assistantDataTitle: dataTitle,
      assistantDataDescription: dataDescription,
      assistantSuggestedQuestions: suggestedQuestions,
      show: false, //rbac.canUseDataAssistant,
    });
    selectedGroupingInterval.value = dateRangeStore.groupingCalendarInterval;
    loadData();
  });

  onBeforeUnmount(() => {
    // todo
    dataAssistantStore.hide();
  });
</script>
<style>
  .p-selectbutton .p-button {
    font-size: 0.8rem;
  }
</style>
